import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { i18n } from '@shared';
import { parseErrorToString } from '@shared';

type AxiosRequestConfigOmitted = Omit<AxiosRequestConfig, 'headers'>;

const API_URL = import.meta.env.VITE_API_URL as string;
const TOKEN_NAME = import.meta.env.VITE_SUPERADMIN_TOKEN_NAME as string;

const config: AxiosRequestConfigOmitted = {
	baseURL: API_URL,
	timeout: 60000,
};

type AxiosInstanceOmitted = Omit<AxiosInstance, 'config'> &
	Omit<AxiosInstance, 'interceptors'>;

type CustomAxiosInstance = AxiosInstanceOmitted & {
	config?: AxiosRequestConfigOmitted;
};

type axiosOmitted = Omit<typeof axios, 'create'>;

type CustomAxios = axiosOmitted & {
	create: (config: AxiosRequestConfigOmitted) => CustomAxiosInstance;
};

const customAxios = axios as CustomAxios;
const axiosInstance: CustomAxiosInstance = customAxios.create(config);

axiosInstance.interceptors.request.use(axiosConfig => {
	const token = localStorage.getItem(TOKEN_NAME);
	if (token) {
		axiosConfig.headers.Authorization = `${token}`;
	}

	axiosConfig.headers['Accept-Language'] = i18n.language;
	if (localStorage.getItem('test-env')) {
		axiosConfig.headers['X-Cypress'] = true;
	}

	return axiosConfig;
});

axiosInstance.interceptors.response.use(
	response => response,
	error => {
		const response = error.response;
		const request = error.request;

		// Handle server responded with an error
		if (response?.data) {
			const { errors, error: serverError } = response.data;

			const sessionExpired = errors?.some(
				(err: { code: number }) => err?.code === 403,
			);

			const isLoggingIn = request.responseURL.includes('login');

			if (sessionExpired && !isLoggingIn) {
				handleJwtExpired(parseErrorToString(response.data));
				throw null;
			}

			if (serverError) {
				throw serverError;
			}

			throw response.data;
		}

		// Handle no response received from server (likely offline)
		if (request && !response) {
			throw new AppError(
				NO_CONNECTION_ERROR_CODE,
				i18n.t('general.error_connecting_to_server'),
				'',
			);
		}

		throw error;
	},
);

const NO_CONNECTION_ERROR_CODE = 9999;
class AppError extends Error {
	code: number;
	details: string;

	constructor(code: number, title: string, details: string) {
		super(title);
		this.code = code;
		this.details = details;

		Object.setPrototypeOf(this, new.target.prototype);
	}
}

export function handleJwtExpired(error: string): void {
	if (window.location.pathname !== '/sessionExpired') {
		window.location.replace(`/sessionExpired?error=${error}`);
	}
}

export default axiosInstance;
